import apiClient from '@/shared/services/apiClient'
import type {
  ApiResponse,
  PricingResponse,
  InitiatePaymentRequest,
  PaymentOrderResponse,
  RazorpayCallbackRequest,
  CallbackResult,
  CurrentSubscriptionResponse,
  ApplyPromoCodeRequest,
  ApplyPromoCodeResponse,
} from '../types'

class PaymentService {
  private readonly baseUrl = '/api/v1/payments'

  /**
   * Get current pricing information
   * GET /payments/pricing
   */
  async getPricing(): Promise<ApiResponse<PricingResponse>> {
    const response = await apiClient.get<ApiResponse<PricingResponse>>(`${this.baseUrl}/pricing`)
    return response.data
  }

  /**
   * Validate a promo code and get price breakdown
   * POST /payments/apply-promo-code
   */
  async applyPromoCode(request: ApplyPromoCodeRequest): Promise<ApiResponse<ApplyPromoCodeResponse>> {
    const response = await apiClient.post<ApiResponse<ApplyPromoCodeResponse>>(
      `${this.baseUrl}/apply-promo-code`,
      request
    )
    return response.data
  }

  /**
   * Create a new payment order
   * POST /payments/orders
   */
  async createOrder(request: InitiatePaymentRequest): Promise<ApiResponse<PaymentOrderResponse>> {
    const response = await apiClient.post<ApiResponse<PaymentOrderResponse>>(
      `${this.baseUrl}/orders`,
      request
    )
    return response.data
  }

  /**
   * Verify payment with Razorpay callback data
   * POST /payments/callbacks/razorpay
   */
  async verifyPayment(request: RazorpayCallbackRequest): Promise<ApiResponse<CallbackResult>> {
    const response = await apiClient.post<ApiResponse<CallbackResult>>(
      `${this.baseUrl}/callbacks/razorpay`,
      request
    )
    return response.data
  }

  /**
   * Cancel an existing order
   * POST /payments/orders/{orderId}/cancel
   */
  async cancelOrder(orderId: string): Promise<ApiResponse<PaymentOrderResponse>> {
    const response = await apiClient.post<ApiResponse<PaymentOrderResponse>>(
      `${this.baseUrl}/orders/${orderId}/cancel`
    )
    return response.data
  }

  /**
   * Get current active subscription for a user
   * GET /payments/subscriptions/current?userId=
   */
  async getCurrentSubscription(userId: string): Promise<ApiResponse<CurrentSubscriptionResponse>> {
    const response = await apiClient.get<ApiResponse<CurrentSubscriptionResponse>>(
      `${this.baseUrl}/subscriptions/current`,
      { params: { userId } }
    )
    return response.data
  }
}

export default new PaymentService()
